import React from "react";
import {
  Box,
  Checkbox,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Card,
} from "@mui/material";
import style from "../searchWorksheet.module.css";
import SearchWorksheetButtons from "../../../components/searchWorksheetButtons";
import { useAppSelector } from "../../../globalState/hooks";
import moment from "moment";
import {
  getBasicDeviationValues,
  getBasicFinalValueFor2Line,
  getBasicFinalValueFor7Line,
} from "../../../utils/getBasicInfoCalculate";
import { getScheduleCFinalObligation } from "../../../utils/getScheduleCCalculate";
import { CalculateNearestChildValues } from "../../../utils/calculateNearestChildValues";
import {
  formatNumber,
  formatNumberWithDigit,
} from "../../../utils/formatNumber";
import { useTranslation } from "react-i18next";
import { calculateLowIncomeDeviation } from "../../../utils/lowIncomeDeviation";

interface TableRow2 {
  id?: string;
  schedules: string;
  attached: boolean;
  notApplicable: boolean;
}

const BasicInformationTab: React.FC = () => {
  const WorkSheetData = useAppSelector((state) => state.createWorksheet);
  const { FinalValue1, FinalValue2 } =
    getBasicFinalValueFor2Line(WorkSheetData);
  const { finalLine7Value1, finalLine7Value2 } =
    getBasicFinalValueFor7Line(WorkSheetData);
  const { deviationValue1, deviationValue2 } =
    getBasicDeviationValues(WorkSheetData);
  const { t } = useTranslation();

  const cellStyleHeading = {
    border: "1px solid #ccc",
    textAlign: "center",
    padding: "15px 20px 15px 10px",
    background: "#F8F8F8",
  };

  const tableData2 = (
    id: string,
    schedules: string,
    attached: boolean,
    notApplicable: boolean
  ): TableRow2 => ({ id, schedules, attached, notApplicable });

  const isGrossIncomeValue =
    WorkSheetData.gross_income_schedule_a?.total_gross_income1 ||
    WorkSheetData.gross_income_schedule_a?.total_gross_income2
      ? true
      : false;

  const isAdjustedIncomeValue =
    Number(
      WorkSheetData.adjusted_income_schedule_b?.monthly_self_employment_income1
    ) > 0 ||
    Number(
      WorkSheetData.adjusted_income_schedule_b?.monthly_self_employment_income2
    ) > 0 ||
    WorkSheetData.adjusted_income_schedule_b?.pre_existing_child_support
      ?.length > 0 ||
    WorkSheetData.adjusted_income_schedule_b
      ?.proposed_adjustment_for_qualified_child?.length > 0
      ? true
      : false;

  const hasParentingTimeData = WorkSheetData.parenting_time_schedule_c?.schedule_c_non_custodial_parenting_days || 
    WorkSheetData.parenting_time_schedule_c?.schedule_c_custodial_parenting_days;
  
  const isBothParentsNCP = WorkSheetData.non_custodial_parent && WorkSheetData.non_custodial_parent.includes(" and ");
  
  const scheduleCFinalObligation = hasParentingTimeData && !isBothParentsNCP ? getScheduleCFinalObligation({
    ...WorkSheetData,
    schedule_c_non_custodial_parenting_days: WorkSheetData.parenting_time_schedule_c?.schedule_c_non_custodial_parenting_days || "",
    schedule_c_custodial_parenting_days: WorkSheetData.parenting_time_schedule_c?.schedule_c_custodial_parenting_days || ""
  }) : 0;

  const isAdditionalExpenses =
    (WorkSheetData.health_insurance_child_care_schedule_d
      ?.health_insurance_premiums_parent1 &&
      WorkSheetData.health_insurance_child_care_schedule_d
        ?.health_insurance_premiums_parent1 !== "0.00") ||
    (WorkSheetData.health_insurance_child_care_schedule_d
      ?.health_insurance_premiums_parent2 &&
      WorkSheetData.health_insurance_child_care_schedule_d
        ?.health_insurance_premiums_parent2 !== "0.00") ||
    (WorkSheetData.health_insurance_child_care_schedule_d
      ?.health_insurance_premiums_parent3 &&
      WorkSheetData.health_insurance_child_care_schedule_d
        ?.health_insurance_premiums_parent3 !== "0.00") ||
    WorkSheetData.health_insurance_child_care_schedule_d?.monthly_total1 !==
      null ||
    WorkSheetData.health_insurance_child_care_schedule_d?.monthly_total2 !==
      null ||
    WorkSheetData.health_insurance_child_care_schedule_d?.monthly_total3 !==
      null
      ? true
      : false;

  const isDeviationsFromPresumptive =
    WorkSheetData.deviations_schedule_e?.proposed_low_income_deviation
      ?.length ||
    WorkSheetData.deviations_schedule_e?.judicial_discretion_for_low_income
      ?.length ||
    WorkSheetData.deviations_schedule_e?.specific_nonspecific_deviation
      ?.length ||
    WorkSheetData.deviations_schedule_e?.extra_ordinary_educational_expenses
      ?.length ||
    WorkSheetData.deviations_schedule_e?.extra_ordinary_medical_expenses
      ?.length ||
    WorkSheetData.deviations_schedule_e?.special_expenses_child_rearing
      ?.length ||
    WorkSheetData.deviations_schedule_e?.parenting_time_deviation1 ||
    WorkSheetData.deviations_schedule_e?.parenting_time_deviation2
      ? // WorkSheetData.deviations_schedule_e?.unjust_or_inappropriate ||
        // WorkSheetData.deviations_schedule_e?.best_interests_of_children ||
        // WorkSheetData.deviations_schedule_e?.impair_ability_to_maintain
        true
      : false;

  const isDeviationsFromPresumptiveForDisplay =
    WorkSheetData.deviations_schedule_e?.proposed_low_income_deviation
      ?.length ||
    WorkSheetData.deviations_schedule_e?.judicial_discretion_for_low_income
      ?.length ||
    WorkSheetData.deviations_schedule_e?.specific_nonspecific_deviation
      ?.length ||
    WorkSheetData.deviations_schedule_e?.extra_ordinary_educational_expenses
      ?.length ||
    WorkSheetData.deviations_schedule_e?.extra_ordinary_medical_expenses
      ?.length ||
    WorkSheetData.deviations_schedule_e?.special_expenses_child_rearing
      ?.length ||
    WorkSheetData.deviations_schedule_e?.parenting_time_deviation1 !== null ||
    WorkSheetData.deviations_schedule_e?.parenting_time_deviation2 !== null
      ? true
      : false;

  const scheduleTableData = [
    tableData2("A", t("Gross Income"), isGrossIncomeValue, !isGrossIncomeValue),
    tableData2(
      "B",
      t("Adjusted Income"),
      isAdjustedIncomeValue,
      !isAdjustedIncomeValue
    ),
    tableData2("C", t("Parenting Time"), hasParentingTimeData && !isBothParentsNCP && scheduleCFinalObligation !== 0, !hasParentingTimeData || isBothParentsNCP || scheduleCFinalObligation === 0),
    tableData2(
      "D",
      t("Additional Expenses"),
      isAdditionalExpenses,
      !isAdditionalExpenses
    ),
    tableData2(
      "E",
      t("Deviations From Presumptive Amount"),
      isDeviationsFromPresumptive,
      !isDeviationsFromPresumptive
    ),
  ];

  const line2ValueTotal =
    FinalValue1 !== undefined && FinalValue2 !== undefined
      ? Number(FinalValue1) + Number(FinalValue2)
      : 0;

  // const IsLine2ValueNegative =
  //   Number(FinalValue1) < 0 || Number(FinalValue2) < 0 || line2ValueTotal === 0;

  const line3Value1 =
    line2ValueTotal > 0
      ? ((Number(FinalValue1 || 0) * 100) / line2ValueTotal).toFixed(2)
      : "0.00";

  const line3Value2 =
    line2ValueTotal > 0
      ? ((Number(FinalValue2 || 0) * 100) / line2ValueTotal).toFixed(2)
      : "0.00";

  const ChildCountNo = WorkSheetData?.add_child_to_worksheet
    ? WorkSheetData?.add_child_to_worksheet?.filter(
        (child) => child.child_status === "included"
      ).length
    : 0;

  const line4NearestValue = CalculateNearestChildValues(
    line2ValueTotal ? Number(line2ValueTotal) : 0,
    ChildCountNo,
    WorkSheetData.created_at
  ).toFixed(2);

  const line5Value1 = (
    Math.round(((Number(line4NearestValue) * Number(line3Value1)) / 100) * 100) /
    100
  ).toFixed(2);

  const line5Value2 = (
    Math.round(((Number(line4NearestValue) * Number(line3Value2)) / 100) * 100) /
    100
  ).toFixed(2);

  // Parenting time adjustment from Schedule C
  const line6Value1 = hasParentingTimeData && !isBothParentsNCP && WorkSheetData.non_custodial_parent === WorkSheetData.parent_name_1 
    ? (scheduleCFinalObligation ? -scheduleCFinalObligation : "")
    : "";
  const line6Value2 = hasParentingTimeData && !isBothParentsNCP && WorkSheetData.non_custodial_parent === WorkSheetData.parent_name_2 
    ? (scheduleCFinalObligation ? -scheduleCFinalObligation : "")
    : "";

  const tempLine7Value1 = Number(finalLine7Value1).toFixed(2);
  const tempLine7Value2 = Number(finalLine7Value2).toFixed(2);

 const line8Value1 =
    line5Value1 || line6Value1 || finalLine7Value1
      ? Number(Number(line5Value1) + Number(line6Value1 || 0) + Number(tempLine7Value1)).toFixed(2)
      : "";

  const line8Value2 =
    line5Value2 || line6Value2 || finalLine7Value2
      ? Number(Number(line5Value2) + Number(line6Value2 || 0) + Number(tempLine7Value2)).toFixed(2)
      : "";

  const Line9Value1 = (
    Number(
      WorkSheetData?.health_insurance_child_care_schedule_d
        ?.health_insurance_premiums_parent1 || 0
    ) /
      12 +
    Number(
      WorkSheetData?.health_insurance_child_care_schedule_d?.monthly_total1 || 0
    )
  ).toFixed(2);

  const Line9Value2 = (
    Number(
      WorkSheetData?.health_insurance_child_care_schedule_d
        ?.health_insurance_premiums_parent2 || 0
    ) /
      12 +
    Number(
      WorkSheetData?.health_insurance_child_care_schedule_d?.monthly_total2 || 0
    )
  ).toFixed(2);

  const line10Value1 = Number(line8Value1) - Number(Line9Value1);
  const line10Value2 = Number(line8Value2) - Number(Line9Value2);

  const lowIncomeResult1 = FinalValue1 && line10Value1
    ? calculateLowIncomeDeviation(Number(FinalValue1), ChildCountNo, line10Value1) : null;
  const lowIncomeResult2 = FinalValue2 && line10Value2
    ? calculateLowIncomeDeviation(Number(FinalValue2), ChildCountNo, line10Value2) : null;

  const line11Value1 = lowIncomeResult1?.deviationApplies ? lowIncomeResult1.cappedAmount.toFixed(2) : line10Value1.toFixed(2);
  const line11Value2 = lowIncomeResult2?.deviationApplies ? lowIncomeResult2.cappedAmount.toFixed(2) : line10Value2.toFixed(2);

  const line12Value1 = isNaN(Number(deviationValue1))
    ? 0
    : Number(deviationValue1);
  const line12Value2 = isNaN(Number(deviationValue2))
    ? 0
    : Number(deviationValue2);

  const line13Value1 = Number(line11Value1) + Number(line12Value1);

  const line13Value2 = Number(line11Value2) + Number(line12Value2);

  const line14Value1 = WorkSheetData.social_security_payment_1;
  const line14Value2 = WorkSheetData.social_security_payment_2;

  const line15Value1 = WorkSheetData.veterans_affairs_disability_payment_1;
  const line15Value2 = WorkSheetData.veterans_affairs_disability_payment_2;

  const line16Value1 = Number(line13Value1) > 0 
    ? Math.max(0, Number(line13Value1) - Number(line14Value1) - Number(line15Value1)) : Number(line13Value1);
  const line16Value2 = Number(line13Value2) > 0 
    ? Math.max(0, Number(line13Value2) - Number(line14Value2) - Number(line15Value2)) : Number(line13Value2);

  // Payment summary logic
  const getPaymentSummary = () => {
    const noncustodialParent = WorkSheetData.non_custodial_parent;
    if (noncustodialParent === null) return "";
    
    // Check if both parents are non-custodial
    if (noncustodialParent.includes(" and ")) {
      return "";
    }
    
    const custodialParent = noncustodialParent === WorkSheetData.parent_name_1 
      ? WorkSheetData.parent_name_2 
      : WorkSheetData.parent_name_1;
    
    const noncustodialValue = noncustodialParent === WorkSheetData.parent_name_1 
      ? line16Value1 
      : line16Value2;
    
    if (noncustodialValue > 0) {
      return `${noncustodialParent} ${t("pays")} ${custodialParent} $${Math.round(noncustodialValue)} ${t("a month")}`;
    } else if (noncustodialValue < 0) {
      return `${custodialParent} ${t("pays")} ${noncustodialParent} $${Math.round(Math.abs(noncustodialValue))} ${t("a month")}`;
    } else {
      return `${noncustodialParent} pays ${custodialParent} $0 a month`;
    }
  };

  return (
    <>
      <Box>
        {WorkSheetData.court_name && WorkSheetData.county_name ? (
          <Box style={{ display: "flex", justifyContent: "center" }}>
            <Typography
              className={style.contentHeading}
              marginTop={"10px !important"}
              style={{
                textTransform: "uppercase",
                textAlign: "center",
                width: "30%",
              }}
            >
              {t(
                "GEORGIA CHILD SUPPORT WORKSHEET {{courtName}} COURT OF {{countyName}} COUNTY STATE OF GEORGIA",
                {
                  courtName: WorkSheetData.court_name,
                  countyName: WorkSheetData.county_name,
                }
              )}
            </Typography>
          </Box>
        ) : null}

        {WorkSheetData.court_name === "Juvenile" ? (
          <>
            <Card
              elevation={0}
              sx={{
                bgcolor: "#F8F8F8",
                border: "1px solid #DFDFDF",
                borderRadius: "5px",
                opacity: 1,
                p: 2,
                mt: 2,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography
                    display={"flex"}
                    justifyContent={"space-between"}
                    sx={{ px: 2, pt: 1 }}
                    color="#404040"
                    fontSize={14}
                    variant="body2"
                  >
                    <div>
                      {" "}
                      <strong>{t("In the Interest of:")}</strong>
                    </div>
                    <div>|</div>
                  </Typography>
                  {WorkSheetData.add_child_to_worksheet.length > 0 &&
                    WorkSheetData.add_child_to_worksheet.map((item, i) => {
                      return (
                        <div key={i}>
                          <Typography
                            display={"flex"}
                            justifyContent={"space-between"}
                            sx={{ px: 2, my: 1 }}
                            color="#404040"
                            fontSize={14}
                          >
                            <div>
                              <strong>{t("Name of Child")}</strong>:
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "40%",
                              }}
                            >
                              <div
                                style={{
                                  textAlign: "justify",
                                  width: "100%",
                                  background: "#DFFFD8",
                                }}
                              >
                                {item.child_name}
                              </div>
                              <div>|</div>
                            </div>
                          </Typography>
                          <Typography
                            display={"flex"}
                            justifyContent={"space-between"}
                            sx={{ px: 2, my: 1 }}
                            color="#404040"
                            fontSize={14}
                          >
                            <div>
                              <strong>{t("SEX")}:</strong>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "40%",
                              }}
                            >
                              <div
                                style={{
                                  textAlign: "justify",
                                  width: "100%",
                                  background: "#DFFFD8",
                                  textTransform: "capitalize",
                                }}
                              >
                                {t(item.sex_of_child)}
                              </div>
                              <div>|</div>
                            </div>
                          </Typography>
                          <Typography
                            display={"flex"}
                            justifyContent={"space-between"}
                            sx={{ px: 2, my: 1 }}
                            color="#404040"
                            fontSize={14}
                          >
                            <div>
                              <strong>{t("DOB")}:</strong>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "40%",
                              }}
                            >
                              <div
                                style={{
                                  textAlign: "justify",
                                  width: "100%",
                                  background: "#DFFFD8",
                                }}
                              >
                                {moment(item.date_of_birth).format(
                                  "MM-DD-YYYY"
                                )}
                              </div>
                              <div>|</div>
                            </div>
                          </Typography>
                        </div>
                      );
                    })}

                  <Typography
                    sx={{ px: 2 }}
                    color="#404040"
                    fontSize={14}
                    display={"flex"}
                    justifyContent={"space-between"}
                  >
                    <div>
                      <strong>{t("Child(ren) Under the Age of 18")}</strong>
                    </div>
                    <div>|</div>
                  </Typography>

                  <Typography
                    sx={{ px: 2, my: 1 }}
                    color="#404040"
                    fontSize={14}
                    display={"flex"}
                    justifyContent={"space-between"}
                  >
                    <div></div>
                    <div>|</div>
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      px: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      display={"flex"}
                      justifyContent={"space-between"}
                      sx={{ bgcolor: "#DFFFD8", width: "80%", p: 1 }}
                    >
                      <div> {WorkSheetData?.plaintiff_name}</div>
                    </Typography>
                    |
                  </Box>
                  <Box sx={{ ml: 3, px: 2 }}>
                    <Typography
                      display={"flex"}
                      justifyContent={"space-between"}
                      variant="body2"
                    >
                      <div style={{ fontWeight: "bold" }}>{t("PLAINTIFF")}</div>{" "}
                      <div>|</div>
                    </Typography>
                    <Typography
                      display={"flex"}
                      justifyContent={"space-between"}
                      variant="body2"
                      align="left"
                      sx={{ mt: 1, ml: 3 }}
                    >
                      <div style={{ fontWeight: "bold" }}> {t("vs.")}</div>
                      <div>|</div>
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      px: 2,
                      mt: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      display={"flex"}
                      justifyContent={"space-between"}
                      sx={{ bgcolor: "#DFFFD8", width: "80%", p: 1 }}
                    >
                      <div> {WorkSheetData?.defendant_name}</div>
                    </Typography>
                    |
                  </Box>

                  <Typography
                    display={"flex"}
                    justifyContent={"space-between"}
                    sx={{ ml: 3, px: 2 }}
                    variant="body2"
                  >
                    <div style={{ fontWeight: "bold" }}>{t("DEFENDANT")}</div>
                    <div>|</div>
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  {WorkSheetData.add_child_to_worksheet.length > 0 &&
                    WorkSheetData.add_child_to_worksheet.map((item, j) => {
                      return (
                        <div key={j}>
                          <Typography
                            display={"flex"}
                            justifyContent={"space-between"}
                            sx={{ px: 2, pt: 3 }}
                            color="#404040"
                            fontSize={14}
                          >
                            <div>
                              <strong>{t("Case No.")}:</strong>
                            </div>
                            <div
                              style={{
                                textAlign: "justify",
                                width: "40%",
                                background: "#DFFFD8",
                              }}
                            >
                              {item.case_number}
                            </div>
                          </Typography>

                          <Typography
                            display={"flex"}
                            justifyContent={"space-between"}
                            sx={{ px: 2, my: 1 }}
                            color="#404040"
                            fontSize={14}
                          >
                            <div>
                              <strong>{t("File No.")}:</strong>
                            </div>
                            <div
                              style={{
                                textAlign: "justify",
                                background: "#DFFFD8",
                                width: "40%",
                              }}
                            >
                              {item.file_number}
                            </div>
                          </Typography>
                        </div>
                      );
                    })}

                  <Typography
                    display={"flex"}
                    justifyContent={"space-between"}
                    sx={{ px: 2, my: 1 }}
                    color="#404040"
                    fontSize={14}
                  >
                    <div>
                      <strong>{t("Worksheet Created At")}:</strong>
                    </div>
                    <div
                      style={{
                        width: "40%",
                        textAlign: "justify",
                        background: "#DFFFD8",
                      }}
                    >
                      {moment(WorkSheetData.created_at).format("MM-DD-YYYY")}
                    </div>
                  </Typography>

                  <Typography
                    display={"flex"}
                    justifyContent={"space-between"}
                    sx={{ px: 2, my: 1 }}
                    color="#404040"
                    fontSize={14}
                  >
                    <div>
                      <strong>{t("Comments to Court")}:</strong>
                    </div>
                    <div
                      style={{
                        width: "40%",
                        textAlign: "justify",
                        background: "#DFFFD8",
                      }}
                    >
                      {WorkSheetData.comments_for_the_court}
                    </div>
                  </Typography>
                </Grid>
              </Grid>
            </Card>
          </>
        ) : (
          <>
            <Grid
              container
              alignItems={{ xs: "start", sm: "center", md: "center" }}
              sx={{
                marginBottom: 2,
                flexDirection: { xs: "column", sm: "row", md: "row" },
              }}
            >
              <Grid item sx={{ pr: 1 }}>
                <Typography variant="body1">
                  <strong>{t("Plaintiff Name :")}</strong>{" "}
                  {WorkSheetData?.plaintiff_name}
                </Typography>
              </Grid>
              <Grid item sx={{ p: { md: 1, sm: 1, xs: 0 } }}>
                <Typography variant="body1">
                  <strong>Vs.</strong>
                </Typography>
              </Grid>
              <Grid item sx={{ p: { md: 1, sm: 1, xs: 0 } }}>
                <Typography variant="body1">
                  <strong>{t("Defendant Name :")}</strong>{" "}
                  {WorkSheetData?.defendant_name}
                </Typography>
              </Grid>
            </Grid>

            <Card
              elevation={0}
              sx={{
                bgcolor: "#F8F8F8",
                border: "1px solid #DFDFDF",
                borderRadius: "5px 5px",
                opacity: 1,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography
                    sx={{ px: 2, pt: 2 }}
                    color="#404040"
                    fontSize={14}
                    variant="body2"
                  >
                    {t("Civil Action Case No.:")}{" "}
                    {WorkSheetData?.civil_action_case_number}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    sx={{ px: 2, pt: 2 }}
                    color="#404040"
                    fontSize={14}
                    variant="body2"
                  >
                    {t("DHS/DCSS Case No.:")} {WorkSheetData?.dhsc_no}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}></Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    sx={{ px: 2, pt: 2 }}
                    color="#404040"
                    fontSize={14}
                    variant="body2"
                  >
                    {t("Worksheet Created At:")}{" "}
                    {moment(WorkSheetData.created_at).format("MM-DD-YYYY")}
                  </Typography>
                </Grid>
              </Grid>
              <Typography
                sx={{ px: 2, pt: 2 }}
                color="#404040"
                fontSize={14}
                variant="body2"
              >
                {t("Comments for the Court:")}
              </Typography>
              <Typography
                sx={{ px: 2, pt: 1, pb: 2 }}
                color="#404040"
                fontSize={14}
                variant="body2"
              >
                {WorkSheetData?.comments_for_the_court}
              </Typography>
            </Card>
          </>
        )}
      </Box>

      <Box sx={{ pt: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography
              color="#404040"
              fontSize={14}
              variant="body2"
              textTransform={"capitalize"}
            >
              {t("Type Of Action:")}{" "}
              {WorkSheetData?.type_of_action
                ? WorkSheetData?.type_of_action
                : "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography color="#404040" fontSize={14} variant="body2">
              {t("Initial Order Date:")}{" "}
              {WorkSheetData?.initial_support_order_date
                ? moment(WorkSheetData?.initial_support_order_date).format(
                    "MM-DD-YYYY"
                  )
                : "N/A"}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ pt: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 350 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: "#404040",
                    color: "#F8F8F8",
                    fontSize: 14,
                  }}
                >
                  #
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#404040",
                    color: "#F8F8F8",
                    fontSize: 14,
                  }}
                >
                  {t("Child Full Name")}
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#404040",
                    color: "#F8F8F8",
                    fontSize: 14,
                  }}
                >
                  {t("Year Of Birth")}
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#404040",
                    color: "#F8F8F8",
                    fontSize: 14,
                  }}
                >
                  {t("Child Status")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {WorkSheetData.add_child_to_worksheet &&
                WorkSheetData.add_child_to_worksheet.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell
                      className={style.cellStyle2}
                      component="th"
                      scope="row"
                      sx={{ backgroundColor: "#CEFBC9" }}
                    >
                      {index + 1}
                    </TableCell>
                    <TableCell
                      className={style.cellStyle2}
                      component="th"
                      scope="row"
                      sx={{ backgroundColor: "#CEFBC9" }}
                    >
                      {row.child_name}
                    </TableCell>
                    <TableCell
                      className={style.cellStyle2}
                      component="th"
                      scope="row"
                      sx={{ backgroundColor: "#CEFBC9" }}
                    >
                      {row.date_of_birth
                        ? moment(row.date_of_birth).format("MM-DD-YYYY")
                        : row.year_of_birth}
                    </TableCell>
                    <TableCell
                      className={style.cellStyle2}
                      component="th"
                      scope="row"
                      sx={{
                        backgroundColor: "#CEFBC9",
                      }}
                    >
                      {row.child_status === "included"
                        ? "Included"
                        : "Excluded"}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box sx={{ pt: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography color="#404040" fontSize={14} variant="body2">
              {t("Number Of Included Children:")}{" "}
              {WorkSheetData?.number_of_included_children}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography color="#404040" fontSize={14} variant="body2">
              {t("Noncustodial Parent:")} {WorkSheetData?.non_custodial_parent}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography color="#404040" fontSize={14} variant="body2">
              {t("Submitted By:")} {WorkSheetData?.make_available_to_court_by}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography color="#404040" fontSize={14} variant="body2">
              {t("Nonparent Custodian:")}{" "}
              {WorkSheetData?.non_parent_custodian_name}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ pt: 4 }}>
        <TableContainer>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell className={style.headerStyle}></TableCell>
                <TableCell className={style.headerStyle}>
                  {WorkSheetData?.parent_name_1}
                </TableCell>
                <TableCell className={style.headerStyle}>
                  {WorkSheetData?.parent_name_2}
                </TableCell>
                <TableCell className={style.headerStyle}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t("1. Monthly Gross Income")}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {WorkSheetData?.gross_income_schedule_a?.total_gross_income1
                    ? "$" +
                      formatNumber(
                        Number(
                          WorkSheetData.gross_income_schedule_a
                            .total_gross_income1
                        )
                      )
                    : ""}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {WorkSheetData?.gross_income_schedule_a?.total_gross_income2
                    ? "$" +
                      formatNumber(
                        Number(
                          WorkSheetData.gross_income_schedule_a
                            .total_gross_income2
                        )
                      )
                    : ""}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {(WorkSheetData?.gross_income_schedule_a
                    ?.total_gross_income1 ||
                    WorkSheetData?.gross_income_schedule_a
                      ?.total_gross_income2) &&
                    "$" +
                      formatNumber(
                        Number(
                          WorkSheetData.gross_income_schedule_a
                            ?.total_gross_income1 || 0
                        ) +
                          Number(
                            WorkSheetData.gross_income_schedule_a
                              ?.total_gross_income2 || 0
                          )
                      )}
                </TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t("2. Monthly Adjusted Income")}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {FinalValue1 ? "$" + formatNumberWithDigit(FinalValue1) : ""}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {FinalValue2 ? "$" + formatNumberWithDigit(FinalValue2) : ""}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {line2ValueTotal
                    ? "$" + formatNumberWithDigit(Number(line2ValueTotal))
                    : ""}
                </TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t("3. Pro Rata Shares of Combined Income")}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {isNaN(Number(line3Value1))
                    ? ""
                    : formatNumberWithDigit(Number(line3Value1)) + "%"}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {isNaN(Number(line3Value2))
                    ? ""
                    : formatNumberWithDigit(Number(line3Value2)) + "%"}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {isNaN(Number(line3Value1)) || isNaN(Number(line3Value2))
                    ? ""
                    : "100.00%"}
                </TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t("4. Basic Child Support Obligation (from the Table)")}
                </TableCell>
                <TableCell
                  className={style.cellStyle}
                  sx={{ backgroundColor: "#F8F8F8" }}
                ></TableCell>
                <TableCell
                  className={style.cellStyle}
                  sx={{ backgroundColor: "#F8F8F8" }}
                ></TableCell>
                <TableCell className={style.cellStyle}>
                  ${formatNumber(Number(line4NearestValue))}
                </TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t("5. Pro rata shares of Basic Child Support Obligation")}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {isNaN(Number(line5Value1))
                    ? ""
                    : "$" + formatNumberWithDigit(Number(line5Value1))}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {isNaN(Number(line5Value2))
                    ? ""
                    : "$" + formatNumberWithDigit(Number(line5Value2))}
                </TableCell>
                <TableCell
                  className={style.cellStyle}
                  sx={{ backgroundColor: "#F8F8F8" }}
                ></TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t("6. Parenting Time Adjustment")}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {line6Value1 !== ""
                  ? "$" + formatNumberWithDigit(Number(line6Value1))
                  : ""}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {line6Value2 !== ""
                  ? "$" + formatNumberWithDigit(Number(line6Value2))
                  : ""}
                </TableCell>
                <TableCell
                  className={style.cellStyle}
                  sx={{ backgroundColor: "#F8F8F8" }}
                ></TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t(
                    "7. Adjustment for Work Related Child Care and Health Insurance Expenses"
                  )}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {finalLine7Value1
                    ? "$" + formatNumberWithDigit(finalLine7Value1)
                    : ""}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {finalLine7Value2
                    ? "$" + formatNumberWithDigit(finalLine7Value2)
                    : ""}
                </TableCell>
                <TableCell
                  className={style.cellStyle}
                  sx={{ backgroundColor: "#F8F8F8" }}
                ></TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t("8. Adjusted Child Support Obligation")}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {isNaN(Number(line8Value1))
                    ? ""
                    : "$" + formatNumberWithDigit(Number(line8Value1))}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {isNaN(Number(line8Value2))
                    ? ""
                    : "$" + formatNumberWithDigit(Number(line8Value2))}
                </TableCell>
                <TableCell
                  className={style.cellStyle}
                  sx={{ backgroundColor: "#F8F8F8" }}
                ></TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t("9. Adjustment for Additional Expenses Paid")}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {Number(Line9Value1) > 0
                    ? "$" + formatNumberWithDigit(Number(Line9Value1))
                    : ""}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {Number(Line9Value2) > 0
                    ? "$" + formatNumberWithDigit(Number(Line9Value2))
                    : ""}
                </TableCell>
                <TableCell
                  className={style.cellStyle}
                  sx={{ backgroundColor: "#F8F8F8" }}
                ></TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t("10. Presumptive Amount of Child Support")}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {line10Value1 ? "$" + formatNumberWithDigit(line10Value1) : ""}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {line10Value2 ? "$" + formatNumberWithDigit(line10Value2) : ""}
                </TableCell>
                <TableCell
                  className={style.cellStyle}
                  sx={{ backgroundColor: "#F8F8F8" }}
                ></TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell
                  className="custom-table-cell-head"
                  colSpan={4}
                  sx={cellStyleHeading}
                >
                  <Typography fontWeight="bold" align="center" color="#404040">
                    {t(
                      "The Amount on Line 10 is the Presumptive Child Support Amount"
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t("11. Subtotal with Low Income Adjustment")}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {line11Value1 ? "$" + formatNumber(Number(line11Value1)) : ""}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {line11Value2 ? "$" + formatNumber(Number(line11Value2)) : ""}
                </TableCell>
                <TableCell
                  className={style.cellStyle}
                  sx={{ backgroundColor: "#F8F8F8" }}
                ></TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t("12. Deviations From Presumptive Child Support Amount")}:
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {isDeviationsFromPresumptiveForDisplay &&
                  line12Value1 !== null &&
                  line12Value1 !== undefined
                    ? "$" + formatNumberWithDigit(line12Value1)
                    : ""}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {isDeviationsFromPresumptiveForDisplay &&
                  line12Value2 !== null &&
                  line12Value2 !== undefined
                    ? "$" + formatNumberWithDigit(line12Value2)
                    : ""}
                </TableCell>
                <TableCell
                  className={style.cellStyle}
                  sx={{ backgroundColor: "#F8F8F8" }}
                ></TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t("13. Subtotal")}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {line13Value1
                    ? "$" + formatNumberWithDigit(line13Value1)
                    : ""}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {line13Value2
                    ? "$" + formatNumberWithDigit(line13Value2)
                    : ""}
                </TableCell>
                <TableCell
                  className={style.cellStyle}
                  sx={{ backgroundColor: "#F8F8F8" }}
                ></TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t(
                    "14. Social Security Payments to Children (excludes Supplemental Security Income (SSI))"
                  )}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {line14Value1 ? "$" + formatNumber(Number(line14Value1)) : ""}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {line14Value2 ? "$" + formatNumber(Number(line14Value2)) : ""}
                </TableCell>
                <TableCell
                  className={style.cellStyle}
                  sx={{ backgroundColor: "#F8F8F8" }}
                ></TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t("15. Veterans Affairs Disability Payments to Children")}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {line15Value1 ? "$" + formatNumber(Number(line15Value1)) : ""}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {line15Value2 ? "$" + formatNumber(Number(line15Value2)) : ""}
                </TableCell>
                <TableCell
                  className={style.cellStyle}
                  sx={{ backgroundColor: "#F8F8F8" }}
                ></TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t(
                    "16. Final Monthly Child Support Amount (rounded to whole number)"
                  )}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  ${formatNumberWithDigit(Math.round(line16Value1))}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  ${formatNumberWithDigit(Math.round(line16Value2))}
                </TableCell>
                <TableCell
                  className={style.cellStyle}
                  sx={{ backgroundColor: "#F8F8F8" }}
                ></TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell
                  className="custom-table-cell-head"
                  colSpan={4}
                  sx={cellStyleHeading}
                >
                  <Typography fontWeight="bold" align="center" color="#404040">
                    {t(
                      "The Amount on Line 16 is the Final Child Support Amount"
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
            <TableBody>
              <TableRow>
                <TableCell className={style.firstColumnStyle}>
                  {t(
                    "17. Percentages for each parent for future Uninsured Health Expenses"
                  )}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {WorkSheetData.uninsured_health_expenses_1
                    ? Number(WorkSheetData.uninsured_health_expenses_1).toFixed(
                        2
                      ) + "%"
                    : ""}
                </TableCell>
                <TableCell className={style.cellStyle}>
                  {WorkSheetData.uninsured_health_expenses_2
                    ? Number(WorkSheetData.uninsured_health_expenses_2).toFixed(
                        2
                      ) + "%"
                    : ""}
                </TableCell>
                <TableCell
                  className={style.cellStyle}
                  sx={{ backgroundColor: "#F8F8F8" }}
                ></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      
      {/* Payment Summary */}
      <Box sx={{ pt: 2, textAlign: 'center' }}>
        <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: 32 }}>
          {getPaymentSummary()}
        </Typography>
      </Box>

      <Box sx={{ pt: 4 }}>
        <TableContainer>
          <Table sx={{ minWidth: 450 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: "#404040",
                    color: "#F8F8F8",
                    border: "1px solid #DFDFDF",
                  }}
                ></TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#404040",
                    color: "#F8F8F8",
                    border: "1px solid #DFDFDF",
                  }}
                >
                  {t("Schedules")}
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#404040",
                    color: "#F8F8F8",
                    border: "1px solid #DFDFDF",
                  }}
                >
                  {t("Attached")}
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#404040",
                    color: "#F8F8F8",
                    border: "1px solid #DFDFDF",
                  }}
                >
                  {t("Not Applicable")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scheduleTableData.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className={style.cellStyle}>{row.id}</TableCell>
                  <TableCell className={style.cellStyle}>
                    {row.schedules}
                  </TableCell>
                  <TableCell className={style.cellStyle}>
                    <Checkbox checked={row.attached} disabled />
                  </TableCell>
                  <TableCell className={style.cellStyle}>
                    <Checkbox checked={row.notApplicable} disabled />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <SearchWorksheetButtons />
      </Box>
    </>
  );
};

export default BasicInformationTab;
